import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  Clock,
  Pencil,
  Check,
  X,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  CloudRain,
  GripVertical,
  CalendarDays,
  Printer,
  Tag,
} from 'lucide-react';
import { formatTimeDisplay, capitalizeFirstWord } from '@peles/shared';
import type { TimelineSection as TSection, TimelineEntry } from '@peles/shared';
import {
  useTimeline,
  useUpdateTimeline,
  useUpdateTimelineSection,
  useAddTimelineSection,
  useDeleteTimelineSection,
  useAddTimelineEntry,
  useUpdateTimelineEntry,
  useDeleteTimelineEntry,
} from '../hooks/useEventOps';
import { useQuery } from '@tanstack/react-query';
import { useEventTags, useEvent } from '../hooks/useEvents';
import { getAppLogoUrl } from '../api/events';
import { TimelinePrintView } from '../components/timeline/TimelinePrintView';
import { useDialog } from '../context/DialogContext';

// ─── Time helpers ─────────────────────────────────────────────────────────────

// Re-exported for existing consumers/tests; the implementation lives in @peles/shared.
export { formatTimeDisplay };

export function timeToInputValue(time: string | null | undefined): string {
  if (!time) return '';
  if (/^\d{2}:\d{2}$/.test(time)) return time;
  const match = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match) {
    let h = parseInt(match[1]);
    const m = match[2];
    if (match[3].toUpperCase() === 'PM' && h < 12) h += 12;
    if (match[3].toUpperCase() === 'AM' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${m}`;
  }
  return '';
}

// ─── Entry form state ─────────────────────────────────────────────────────────

const BLANK_ENTRY = { time: '', title: '', location: '', vendorNote: '' };
type EntryFormState = typeof BLANK_ENTRY;

// ─── Main page ────────────────────────────────────────────────────────────────

export function TimelineEditPage() {
  const { timelineId, eventId } = useParams<{ timelineId: string; eventId: string }>();
  const navigate = useNavigate();
  const { confirm } = useDialog();
  const { data: timeline, isLoading } = useTimeline(timelineId!);
  const { data: eventTags = [] } = useEventTags(eventId);
  const { data: event } = useEvent(eventId ?? '');
  const { data: appLogoUrl } = useQuery({
    queryKey: ['app-logo-url'],
    queryFn: getAppLogoUrl,
    staleTime: 60_000,
  });

  const [showPrint, setShowPrint] = useState(false);

  const updateTimeline = useUpdateTimeline();
  const updateSection = useUpdateTimelineSection();
  const addSection = useAddTimelineSection();
  const deleteSection = useDeleteTimelineSection();
  const addEntry = useAddTimelineEntry();
  const updateEntry = useUpdateTimelineEntry();
  const deleteEntry = useDeleteTimelineEntry();

  const isTemplate = timeline?.isTemplate ?? false;
  const backPath = eventId ? `/events/${eventId}/timeline` : '/templates';

  // ─── Local sorted state (drives both rendering and DnD) ──────────────────

  const [localSections, setLocalSections] = useState<TSection[]>([]);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (timeline?.sections && !isDraggingRef.current) {
      setLocalSections(
        [...timeline.sections]
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map((s) => ({
            ...s,
            entries: [...s.entries].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
          })),
      );
    }
  }, [timeline]);

  // ─── DnD helpers ─────────────────────────────────────────────────────────

  const sectionIds = useMemo(() => new Set(localSections.map((s) => s.id)), [localSections]);
  const entryToSection = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of localSections) for (const e of s.entries) map.set(e.id, s.id);
    return map;
  }, [localSections]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const originalSectionsRef = useRef<TSection[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function getActiveItem() {
    if (!activeId) return null;
    if (sectionIds.has(activeId)) {
      return { kind: 'section' as const, data: localSections.find((s) => s.id === activeId) };
    }
    const sectionId = entryToSection.get(activeId);
    if (sectionId) {
      const section = localSections.find((s) => s.id === sectionId);
      const entry = section?.entries.find((e) => e.id === activeId);
      return { kind: 'entry' as const, data: entry };
    }
    return null;
  }

  function handleDragStart({ active }: DragStartEvent) {
    isDraggingRef.current = true;
    setActiveId(String(active.id));
    originalSectionsRef.current = localSections;
  }

  function handleDragOver({ active, over }: DragOverEvent) {
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (sectionIds.has(activeId)) return;

    const activeSectionId = entryToSection.get(activeId);
    if (!activeSectionId) return;

    const overSectionId = sectionIds.has(overId) ? overId : (entryToSection.get(overId) ?? null);
    if (!overSectionId || overSectionId === activeSectionId) return;

    setLocalSections((prev) => {
      const activeEntry = prev
        .find((s) => s.id === activeSectionId)
        ?.entries.find((e) => e.id === activeId);
      if (!activeEntry) return prev;

      const overIdx = !sectionIds.has(overId)
        ? (prev.find((s) => s.id === overSectionId)?.entries.findIndex((e) => e.id === overId) ??
          -1)
        : -1;

      return prev.map((s) => {
        if (s.id === activeSectionId) {
          return { ...s, entries: s.entries.filter((e) => e.id !== activeId) };
        }
        if (s.id === overSectionId) {
          const entries = [...s.entries];
          const insertAt = overIdx >= 0 ? overIdx : entries.length;
          entries.splice(insertAt, 0, activeEntry);
          return { ...s, entries };
        }
        return s;
      });
    });
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    isDraggingRef.current = false;
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    if (sectionIds.has(activeId)) {
      if (!sectionIds.has(overId)) return;
      setLocalSections((prev) => {
        const fromIdx = prev.findIndex((s) => s.id === activeId);
        const toIdx = prev.findIndex((s) => s.id === overId);
        if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return prev;
        const reordered = arrayMove(prev, fromIdx, toIdx);
        reordered.forEach((s, i) => {
          if ((s.sortOrder ?? 0) !== i) {
            updateSection.mutate({ sectionId: s.id, updates: { sortOrder: i } });
          }
        });
        return reordered.map((s, i) => ({ ...s, sortOrder: i }));
      });
    } else {
      const targetSectionId = entryToSection.get(activeId);
      if (!targetSectionId) return;

      setLocalSections((prev) => {
        const targetSection = prev.find((s) => s.id === targetSectionId);
        if (!targetSection) return prev;

        const fromIdx = targetSection.entries.findIndex((e) => e.id === activeId);
        const toIdx = !sectionIds.has(overId)
          ? targetSection.entries.findIndex((e) => e.id === overId)
          : targetSection.entries.length - 1;

        let finalEntries = targetSection.entries;
        if (fromIdx >= 0 && toIdx >= 0 && fromIdx !== toIdx) {
          finalEntries = arrayMove(targetSection.entries, fromIdx, toIdx);
        }

        const originalSectionId = originalSectionsRef.current.find((s) =>
          s.entries.some((e) => e.id === activeId),
        )?.id;
        const crossSection = originalSectionId && originalSectionId !== targetSectionId;

        finalEntries.forEach((e, i) => {
          const moved = e.id === activeId && crossSection;
          updateEntry.mutate({
            entryId: e.id,
            updates: {
              sortOrder: i,
              ...(moved ? { sectionId: targetSectionId } : {}),
            },
          });
        });

        if (crossSection && originalSectionId) {
          const sourceSection = prev.find((s) => s.id === originalSectionId);
          sourceSection?.entries.forEach((e, i) => {
            updateEntry.mutate({ entryId: e.id, updates: { sortOrder: i } });
          });
        }

        return prev.map((s) =>
          s.id === targetSectionId
            ? { ...s, entries: finalEntries.map((e, i) => ({ ...e, sortOrder: i })) }
            : s,
        );
      });
    }
  }

  // ─── Name editing ─────────────────────────────────────────────────────────

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');

  const handleStartEditName = useCallback(() => {
    if (!timeline) return;
    setNameValue(timeline.templateName || timeline.title || '');
    setEditingName(true);
  }, [timeline]);

  const handleSaveName = useCallback(() => {
    if (!timeline || !nameValue.trim()) return;
    const cleanName = capitalizeFirstWord(nameValue.trim());
    const updates = isTemplate ? { templateName: cleanName } : { title: cleanName };
    updateTimeline.mutate({ id: timeline.id, updates });
    setEditingName(false);
  }, [timeline, nameValue, isTemplate, updateTimeline]);

  // ─── Date editing ─────────────────────────────────────────────────────────

  const handleDateChange = useCallback(
    (value: string) => {
      if (!timeline) return;
      updateTimeline.mutate({
        id: timeline.id,
        updates: { eventDate: value ? new Date(value).toISOString() : null },
      });
    },
    [timeline, updateTimeline],
  );

  // ─── Sections ────────────────────────────────────────────────────────────

  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [newSectionName, setNewSectionName] = useState('');

  const toggleSection = useCallback((id: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleAddSection = useCallback(() => {
    if (!timeline || !newSectionName.trim()) return;
    addSection.mutate(
      { timelineId: timeline.id, name: capitalizeFirstWord(newSectionName.trim()) },
      { onSuccess: () => setNewSectionName('') },
    );
  }, [timeline, newSectionName, addSection]);

  const handleDeleteSection = useCallback(
    async (sectionId: string) => {
      if (
        !(await confirm({
          title: 'Delete Section',
          message: 'Delete this section and all its entries?',
          confirmLabel: 'Delete',
        }))
      )
        return;
      deleteSection.mutate(sectionId);
    },
    [deleteSection, confirm],
  );

  // ─── Entries ──────────────────────────────────────────────────────────────

  const [addingEntryTo, setAddingEntryTo] = useState<string | null>(null);
  const [entryForm, setEntryForm] = useState<EntryFormState>(BLANK_ENTRY);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editEntryForm, setEditEntryForm] = useState<EntryFormState>(BLANK_ENTRY);

  const startAddEntry = useCallback(
    (sectionId: string) => {
      const section = localSections.find((s) => s.id === sectionId);
      const lastEntry = section?.entries
        .slice()
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .at(-1);
      const prefillTime = lastEntry?.time ? timeToInputValue(lastEntry.time) : '';
      setAddingEntryTo(sectionId);
      setEntryForm({ ...BLANK_ENTRY, time: prefillTime });
    },
    [localSections],
  );

  const handleAddEntry = useCallback(() => {
    if (!addingEntryTo || !entryForm.title.trim()) return;
    addEntry.mutate(
      {
        sectionId: addingEntryTo,
        time: entryForm.time || undefined,
        title: capitalizeFirstWord(entryForm.title.trim()),
        location: entryForm.location || undefined,
        vendorNote: entryForm.vendorNote || undefined,
      },
      {
        onSuccess: () => {
          setEntryForm(BLANK_ENTRY);
          setAddingEntryTo(null);
        },
      },
    );
  }, [addingEntryTo, entryForm, addEntry]);

  const handleStartEditEntry = useCallback((entry: TimelineEntry) => {
    setEditingEntryId(entry.id);
    setEditEntryForm({
      time: timeToInputValue(entry.time),
      title: entry.title,
      location: entry.location || '',
      vendorNote: entry.vendorNote || '',
    });
  }, []);

  const handleSaveEntry = useCallback(() => {
    if (!editingEntryId || !editEntryForm.title.trim()) return;
    updateEntry.mutate(
      {
        entryId: editingEntryId,
        updates: {
          time: editEntryForm.time || null,
          title: capitalizeFirstWord(editEntryForm.title.trim()),
          location: editEntryForm.location || null,
          vendorNote: editEntryForm.vendorNote || null,
        },
      },
      { onSuccess: () => setEditingEntryId(null) },
    );
  }, [editingEntryId, editEntryForm, updateEntry]);

  const handleUpdateRainPlan = useCallback(
    (value: string) => {
      if (!timeline) return;
      updateTimeline.mutate({ id: timeline.id, updates: { rainPlanNotes: value } });
    },
    [timeline, updateTimeline],
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return <div className="p-6 text-gray-500">Loading...</div>;
  }
  if (!timeline) {
    return (
      <div className="p-6 text-red-500" data-testid="not-found">
        Timeline not found.
      </div>
    );
  }

  const activeItem = getActiveItem();
  const displayName = isTemplate
    ? timeline.templateName || timeline.title || 'Untitled Template'
    : timeline.title || 'Untitled Timeline';

  // Convert stored ISO date to YYYY-MM-DD for <input type="date">
  const dateInputValue = timeline.eventDate
    ? new Date(timeline.eventDate).toISOString().slice(0, 10)
    : '';

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {/* Header — hidden when printing (TimelinePrintView owns the print output) */}
      <div className="print:hidden">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(backPath)}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Back"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
          </button>

          {editingName ? (
            <div className="flex items-center gap-2 flex-1" data-testid="name-edit-form">
              <input
                type="text"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') setEditingName(false);
                }}
                autoFocus
                className="text-2xl font-bold text-gray-900 border-b-2 border-primary-500 outline-none bg-transparent flex-1"
                data-testid="name-input"
              />
              <button
                onClick={handleSaveName}
                disabled={!nameValue.trim()}
                className="text-green-600 hover:text-green-700 disabled:opacity-40 p-1"
                aria-label="Save name"
                data-testid="save-name-button"
              >
                <Check size={18} />
              </button>
              <button
                onClick={() => setEditingName(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
                aria-label="Cancel name edit"
                data-testid="cancel-name-button"
              >
                <X size={18} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-1">
              <h1 className="text-2xl font-bold text-gray-900">
                {capitalizeFirstWord(displayName)}
              </h1>
              <button
                onClick={handleStartEditName}
                className="text-gray-400 hover:text-gray-600 p-1"
                aria-label="Edit name"
                data-testid="edit-name-button"
              >
                <Pencil size={16} />
              </button>
            </div>
          )}

          <div className="flex items-center gap-2 shrink-0">
            {eventId && (
              <button
                onClick={() => setShowPrint(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
                title="Print timeline"
                data-testid="print-timeline-button"
              >
                <Printer size={15} />
                Print
              </button>
            )}
            <div className="flex items-center gap-1.5 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-md">
              <Clock size={12} />
              {isTemplate ? 'Timeline Template' : 'Timeline'}
            </div>
          </div>
        </div>

        {/* Date + Rain Plan row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Timeline date */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
              <CalendarDays size={14} />
              Timeline Date
            </div>
            <input
              type="date"
              value={dateInputValue}
              onChange={(e) => handleDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              data-testid="timeline-date-input"
            />
          </div>

          {/* Rain Plan Notes */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
              <CloudRain size={14} />
              Rain Plan Notes
            </div>
            <textarea
              value={timeline.rainPlanNotes || ''}
              onChange={(e) => handleUpdateRainPlan(e.target.value)}
              placeholder="Enter rain plan notes..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
              data-testid="rain-plan-notes"
            />
          </div>
        </div>

        {/* Main layout: sections + optional tag sidebar */}
        <div className={eventId && eventTags.length > 0 ? 'flex gap-4 items-start' : ''}>
          {/* Sections */}
          <div className="flex-1 space-y-3 min-w-0">
            {localSections.length === 0 && (
              <div className="text-center py-8 bg-white rounded-lg shadow">
                <Clock className="mx-auto h-10 w-10 text-gray-300" />
                <p className="mt-2 text-sm text-gray-400">No sections yet. Add one below.</p>
              </div>
            )}

            <SortableContext
              items={localSections.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {localSections.map((section) => (
                <SortableSection
                  key={section.id}
                  section={section}
                  expanded={expandedSections.has(section.id)}
                  onToggle={toggleSection}
                  onDeleteSection={handleDeleteSection}
                  addingEntryTo={addingEntryTo}
                  entryForm={entryForm}
                  onChangeEntryForm={setEntryForm}
                  onStartAddEntry={startAddEntry}
                  onAddEntry={handleAddEntry}
                  onCancelAdd={() => setAddingEntryTo(null)}
                  editingEntryId={editingEntryId}
                  editEntryForm={editEntryForm}
                  onChangeEditEntryForm={setEditEntryForm}
                  onStartEditEntry={handleStartEditEntry}
                  onSaveEntry={handleSaveEntry}
                  onCancelEdit={() => setEditingEntryId(null)}
                  onDeleteEntry={(id) => deleteEntry.mutate(id)}
                  addingPending={addEntry.isPending}
                  savingPending={updateEntry.isPending}
                />
              ))}
            </SortableContext>

            {/* Add Section */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddSection()}
                placeholder="New section name"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                data-testid="new-section-input"
              />
              <button
                onClick={handleAddSection}
                disabled={!newSectionName.trim()}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 disabled:opacity-50"
                data-testid="add-section-button"
              >
                Add Section
              </button>
            </div>
          </div>

          {/* Tag sidebar — only when editing an event timeline with tags */}
          {eventId && eventTags.length > 0 && (
            <div className="w-56 shrink-0 bg-white rounded-lg shadow p-3" data-testid="tag-sidebar">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                <Tag size={12} />
                Available Tags
              </div>
              <p className="text-xs text-gray-400 mb-3">
                Use <code className="bg-gray-100 px-1 rounded">{'{CODE}'}</code> in text fields.
              </p>
              <div className="space-y-1.5">
                {eventTags.map((tag) => (
                  <div key={tag.id} className="group" data-testid={`sidebar-tag-${tag.code}`}>
                    <button
                      onClick={() => navigator.clipboard.writeText(`{${tag.code}}`)}
                      className="w-full text-left"
                      title={`Click to copy {${tag.code}}`}
                    >
                      <code className="text-xs font-mono bg-primary-50 text-primary-700 px-1.5 py-0.5 rounded group-hover:bg-primary-100 transition-colors">
                        {`{${tag.code}}`}
                      </code>
                      <div className="text-xs text-gray-500 mt-0.5 truncate">{tag.description}</div>
                      {tag.value && (
                        <div className="text-xs text-gray-400 truncate">{tag.value}</div>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* end print:hidden wrapper */}

      {/* Print View */}
      {showPrint && timeline && (
        <TimelinePrintView
          timeline={timeline}
          eventName={event?.name ?? 'Event'}
          eventTags={eventTags}
          appLogoUrl={appLogoUrl ?? null}
          eventLogoUrl={event?.logoUrl ?? null}
          onClose={() => setShowPrint(false)}
        />
      )}

      {/* Drag Overlay */}
      <DragOverlay>
        {activeItem?.kind === 'section' && activeItem.data && (
          <div className="bg-white rounded-lg shadow-lg border-2 border-primary-300 px-4 py-3 opacity-90">
            <span className="font-medium text-sm">{activeItem.data.name}</span>
          </div>
        )}
        {activeItem?.kind === 'entry' && activeItem.data && (
          <div className="bg-white rounded-md shadow-lg border border-primary-300 px-3 py-2 opacity-90 flex items-center gap-3">
            <span className="text-sm font-mono text-primary-600 w-16 shrink-0">
              {formatTimeDisplay(activeItem.data.time) || '—'}
            </span>
            <span className="text-sm font-medium">
              {capitalizeFirstWord(activeItem.data.title)}
            </span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

// ─── Sortable Section ─────────────────────────────────────────────────────────

interface SortableSectionProps {
  section: TSection;
  expanded: boolean;
  onToggle: (id: string) => void;
  onDeleteSection: (id: string) => void;
  addingEntryTo: string | null;
  entryForm: EntryFormState;
  onChangeEntryForm: (v: EntryFormState) => void;
  onStartAddEntry: (sectionId: string) => void;
  onAddEntry: () => void;
  onCancelAdd: () => void;
  editingEntryId: string | null;
  editEntryForm: EntryFormState;
  onChangeEditEntryForm: (v: EntryFormState) => void;
  onStartEditEntry: (entry: TimelineEntry) => void;
  onSaveEntry: () => void;
  onCancelEdit: () => void;
  onDeleteEntry: (id: string) => void;
  addingPending: boolean;
  savingPending: boolean;
}

function SortableSection(props: SortableSectionProps) {
  const { section, expanded, onToggle, onDeleteSection } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-lg shadow"
      data-testid={`section-${section.id}`}
    >
      <div className="flex items-center gap-1 px-3 py-3">
        <button
          {...attributes}
          {...listeners}
          className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing p-0.5 touch-none"
          aria-label="Drag section"
          data-testid={`drag-section-${section.id}`}
        >
          <GripVertical size={16} />
        </button>

        <button
          onClick={() => onToggle(section.id)}
          className="flex items-center gap-2 flex-1 text-left hover:bg-gray-50 rounded px-1 py-0.5"
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="font-medium text-sm">{capitalizeFirstWord(section.name)}</span>
          <span className="text-xs text-gray-400">({section.entries.length} entries)</span>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onDeleteSection(section.id);
          }}
          className="text-gray-400 hover:text-red-600 p-1"
          aria-label="Delete section"
          data-testid={`delete-section-${section.id}`}
        >
          <Trash2 size={14} />
        </button>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3">
          <SortableContext
            items={section.entries.map((e) => e.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-0.5 mb-3">
              {section.entries.map((entry) =>
                props.editingEntryId === entry.id ? (
                  <EntryEditForm
                    key={entry.id}
                    form={props.editEntryForm}
                    onChange={props.onChangeEditEntryForm}
                    onSave={props.onSaveEntry}
                    onCancel={props.onCancelEdit}
                    saving={props.savingPending}
                    testId={`edit-entry-form-${entry.id}`}
                  />
                ) : (
                  <SortableEntry
                    key={entry.id}
                    entry={entry}
                    onEdit={props.onStartEditEntry}
                    onDelete={props.onDeleteEntry}
                  />
                ),
              )}
            </div>
          </SortableContext>

          {props.addingEntryTo === section.id ? (
            <EntryEditForm
              form={props.entryForm}
              onChange={props.onChangeEntryForm}
              onSave={props.onAddEntry}
              onCancel={props.onCancelAdd}
              saving={props.addingPending}
              testId="add-entry-form"
            />
          ) : (
            <button
              onClick={() => props.onStartAddEntry(section.id)}
              className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
              data-testid={`add-entry-button-${section.id}`}
            >
              <Plus size={12} />
              Add Entry
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sortable Entry ───────────────────────────────────────────────────────────

function SortableEntry({
  entry,
  onEdit,
  onDelete,
}: {
  entry: TimelineEntry;
  onEdit: (entry: TimelineEntry) => void;
  onDelete: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 py-1.5 border-b border-gray-50 last:border-0 group"
      data-testid={`entry-${entry.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing p-0.5 touch-none mt-0.5 shrink-0"
        aria-label="Drag entry"
        data-testid={`drag-entry-${entry.id}`}
      >
        <GripVertical size={12} />
      </button>
      <span className="text-sm font-mono text-primary-600 w-16 shrink-0">
        {formatTimeDisplay(entry.time) || '—'}
      </span>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium">{capitalizeFirstWord(entry.title)}</span>
        {entry.location && <span className="text-xs text-gray-500 ml-2">@ {entry.location}</span>}
        {entry.vendorNote && (
          <span className="text-xs text-gray-400 ml-2">({entry.vendorNote})</span>
        )}
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <button
          onClick={() => onEdit(entry)}
          className="text-gray-400 hover:text-primary-600 p-0.5"
          aria-label="Edit entry"
          data-testid={`edit-entry-${entry.id}`}
        >
          <Pencil size={12} />
        </button>
        <button
          onClick={() => onDelete(entry.id)}
          className="text-gray-400 hover:text-red-600 p-0.5"
          aria-label="Delete entry"
          data-testid={`delete-entry-${entry.id}`}
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

// ─── Entry edit / add form ────────────────────────────────────────────────────

function EntryEditForm({
  form,
  onChange,
  onSave,
  onCancel,
  saving,
  testId,
}: {
  form: EntryFormState;
  onChange: (v: EntryFormState) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  testId?: string;
}) {
  return (
    <div className="py-2 space-y-2" data-testid={testId}>
      <div className="flex gap-2">
        <input
          type="time"
          value={form.time}
          onChange={(e) => onChange({ ...form, time: e.target.value })}
          className="w-32 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          data-testid="entry-time-input"
        />
        <input
          type="text"
          value={form.title}
          onChange={(e) => onChange({ ...form, title: e.target.value })}
          placeholder="Title"
          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          data-testid="entry-title-input"
        />
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={form.location}
          onChange={(e) => onChange({ ...form, location: e.target.value })}
          placeholder="Location"
          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          data-testid="entry-location-input"
        />
        <input
          type="text"
          value={form.vendorNote}
          onChange={(e) => onChange({ ...form, vendorNote: e.target.value })}
          placeholder="Vendor note"
          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          data-testid="entry-vendor-note-input"
        />
      </div>
      <div className="flex gap-2">
        <button
          onClick={onSave}
          disabled={saving || !form.title.trim()}
          className="px-3 py-1.5 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 disabled:opacity-50"
          data-testid="entry-save-button"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50"
          data-testid="entry-cancel-button"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
